import { Component, type ErrorInfo, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { Button } from './Button';

/**
 * B-03 (23 Sep): one missing API field blanked a whole page. The Saved
 * Reports tab called `schedule.output_format.toUpperCase()` on a field the
 * list serializer didn't send; React caught the TypeError, found no
 * boundary above it, and unmounted the entire route -- a white screen, with
 * the reason only in the console. There was no ErrorBoundary anywhere in
 * this app.
 *
 * This is deliberately the whole of it: say something went wrong, offer the
 * way back, and log the error where a developer will find it. It does NOT
 * excuse leaving a render fragile -- the field and the component were both
 * fixed -- it is the floor under the next one.
 */

interface Props {
  children: ReactNode;
  /** Changing this clears a caught error: see RouteErrorBoundary. */
  resetKey?: string;
}

interface State {
  error: Error | null;
  resetKey?: string;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, resetKey: this.props.resetKey };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  /** Navigating away must not leave the panel up on the next page. Note this
   * clears the error WITHOUT remounting the boundary itself, so wrapping a
   * layout in one does not tear the layout down on every navigation. */
  static getDerivedStateFromProps(props: Props, state: State): Partial<State> | null {
    if (props.resetKey !== state.resetKey) {
      return { error: null, resetKey: props.resetKey };
    }
    return null;
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error('Unhandled render error:', error, info.componentStack);
  }

  handleRetry = () => this.setState({ error: null });

  render() {
    if (!this.state.error) return this.props.children;

    return (
      <div role="alert" className="flex flex-col items-center gap-3 px-4 py-16 text-center">
        <ExclamationTriangleIcon className="h-10 w-10 text-red-500 dark:text-red-400" />
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Something went wrong</h2>
        <p className="max-w-md text-sm text-gray-500 dark:text-gray-400">
          This page could not be displayed. Nothing you were looking at has been changed. Try again, and
          tell your administrator if it keeps happening.
        </p>
        <div className="flex flex-wrap items-center justify-center gap-2">
          <Button onClick={this.handleRetry}>Try again</Button>
          <Button variant="outline" onClick={() => window.location.assign('/dashboard')}>
            Go to dashboard
          </Button>
        </div>
      </div>
    );
  }
}

/** The boundary as App.tsx wires it: one per route, cleared on navigation. */
export function RouteErrorBoundary({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  return <ErrorBoundary resetKey={pathname}>{children}</ErrorBoundary>;
}
